import { useCallback } from 'react';

import { HomeworkGradesType } from 'types';
import {
  useGradeHomeworkSubmissionMutation,
  useRerunSubmissionAiReviewMutation,
  useSubmitLessonHomeworkMutation,
  useUpsertLessonHomeworkAssignmentMutation,
} from 'core/graphql';

import { GRADE_TO_WIRE } from './transform';

/** Homework error codes — plain strings by contract (spec 0012 §4). */
export enum HomeworkErrorCode {
  NOT_GROUP_MEMBER = 'NOT_GROUP_MEMBER',
  INVALID_INPUT = 'INVALID_INPUT',
  INVALID_GITHUB_URL = 'INVALID_GITHUB_URL',
  INVALID_FILE = 'INVALID_FILE',
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  ALREADY_SUBMITTED = 'ALREADY_SUBMITTED',
  INVALID_MARK = 'INVALID_MARK',
  NOT_LESSON_TEACHER = 'NOT_LESSON_TEACHER',
  AI_DISABLED = 'AI_DISABLED',
  ASSIGNMENT_EXISTS = 'ASSIGNMENT_EXISTS',
  /** The student re-submitted under the teacher: the grade would have landed
   * on work they never opened, so the server refuses it. */
  STALE_ROSTER = 'STALE_ROSTER',
}

export interface IHomeworkMutationResult {
  success: boolean;
  errorCode: string | null;
}

/** Network errors propagate as thrown ApolloError — callers handle retry UX. */

/**
 * Student submission: exactly one of githubUrl | file. Multipart for `file`
 * rides apollo-upload-client's createUploadLink (already wired in the client).
 * Refetches MyLessonHomework by name — also on a customError (e.g.
 * ALREADY_SUBMITTED means our cached submission state is stale).
 */
export const useSubmitHomework = () => {
  const [mutate, { loading }] = useSubmitLessonHomeworkMutation({
    refetchQueries: ['MyLessonHomework'],
  });

  const submitHomework = useCallback(
    async (
      lessonId: string,
      source: { githubUrl?: string; file?: File },
    ): Promise<IHomeworkMutationResult> => {
      const { data } = await mutate({
        variables: { lessonId, githubUrl: source.githubUrl, file: source.file },
      });
      const payload = data?.submitHomework;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { submitHomework, loading };
};

/**
 * Teacher creates/edits the lesson assignment. Refetches the staff assignment
 * query AND lessonForLms (the per-lesson gate field homeworkAssignmentId lives
 * there — it opens the student tab).
 */
export const useUpsertHomeworkAssignment = () => {
  const [mutate, { loading }] = useUpsertLessonHomeworkAssignmentMutation({
    refetchQueries: ['LmsLessonHomework', 'lessonForLms'],
  });

  const upsertAssignment = useCallback(
    async (
      lessonId: string,
      values: { title: string; text: string; dueDate: string | null },
    ): Promise<IHomeworkMutationResult> => {
      const { data } = await mutate({
        variables: {
          lessonId,
          title: values.title,
          text: values.text,
          dueDate: values.dueDate,
        },
      });
      const payload = data?.upsertHomeworkAssignment;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { upsertAssignment, loading };
};

/**
 * Teacher grades a student (with or without a submission — non-submitters get
 * `not_done`). Takes the existing LMS grade enum and maps it to the wire
 * values in the transform layer. Refetches the roster by operation name.
 *
 * `attempt` — the attempt number the teacher actually SAW. The server answers
 * STALE_ROSTER when it no longer matches, so a grade can never land on a
 * re-submission the teacher never opened. Bulk grading passes none: it runs
 * straight off a freshly loaded roster.
 */
export const useGradeHomework = () => {
  const [mutate, { loading }] = useGradeHomeworkSubmissionMutation({
    refetchQueries: ['LmsHomeworkRoster'],
  });

  const gradeHomework = useCallback(
    async (
      lessonId: string,
      studentId: string,
      grade: HomeworkGradesType,
      comment: string,
      attempt?: number | null,
    ): Promise<IHomeworkMutationResult> => {
      const { data } = await mutate({
        variables: {
          lessonId,
          studentId,
          hwMark: GRADE_TO_WIRE[grade],
          comment: comment || null,
          attempt: attempt ?? null,
        },
      });
      const payload = data?.gradeHomework;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { gradeHomework, loading };
};

/**
 * Teacher re-runs the AI review (reset → pending + enqueue). AI_DISABLED comes
 * back as a customError code when the backend flag is off. Refetches the
 * roster so the drawer picks up the new pending status.
 */
export const useRerunHomeworkAiReview = () => {
  const [mutate, { loading }] = useRerunSubmissionAiReviewMutation({
    refetchQueries: ['LmsHomeworkRoster'],
  });

  const rerunAiReview = useCallback(
    async (submissionId: string): Promise<IHomeworkMutationResult> => {
      const { data } = await mutate({ variables: { submissionId } });
      const payload = data?.rerunHomeworkAiReview;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { rerunAiReview, loading };
};
